import axios from 'axios'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const API_BASE_URL = process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    throw error
  }
)

/**
 * Fetch organization name by ID
 * @param {string} organizationId - Organization ID
 * @returns {Promise<string>} Organization name
 */
export const fetchOrganizationName = async (organizationId) => {
  try {
    if (!organizationId) return 'N/A'

    // Normalize ID: support MongoDB-like object { $oid: "..." }
    const orgId = typeof organizationId === 'object' && organizationId?.$oid
      ? organizationId.$oid
      : String(organizationId)

    // Check if ID is a numeric code (like "885566037") or a MongoDB ObjectId
    // Numeric codes are used in billing records and some invoice migrations
    const isNumericCode = /^\d+$/.test(orgId)
    const endpoint = isNumericCode ? `/organizations/by-code/${orgId}` : `/organizations/${orgId}`

    const response = await apiClient.get(endpoint)
    // Handle different response formats
    const data = response.data?.data || response.data
    const orgName = data?.organizationName || data?.name
    console.log('Fetched organization:', orgId, '->', orgName, `(via ${endpoint})`)
    return orgName || orgId
  } catch (error) {
    console.error('Error fetching organization name:', organizationId, error)
    return String(organizationId)
  }
}

/**
 * Fetch full organization details (name, address, GST, bank info)
 * Primary source: Organizations API (includes businessInfo from database)
 * Fallback: Zoho data for enrichment if needed
 * @param {string} organizationId - Organization ID (MongoDB ObjectId or plain string)
 * @returns {Promise<Object|null>} Organization details or null on failure
 */
export const fetchOrganizationDetails = async (organizationId) => {
  try {
    if (!organizationId) return null

    // Normalize ID: support MongoDB-like object { $oid: "..." }
    const orgId =
      typeof organizationId === 'object' && organizationId?.$oid
        ? organizationId.$oid
        : String(organizationId)

    // Check if ID is a numeric code or ObjectId
    const isNumericCode = /^\d+$/.test(orgId)
    const endpoint = isNumericCode ? `/organizations/by-code/${orgId}` : `/organizations/${orgId}`

    // 1) PRIMARY SOURCE: Load organization details from the Organizations API (DE-Universal-Backend)
    // This endpoint now returns businessInfo directly from the database (nameOfTheBank, ifscCode, etc.)
    const orgResp = await apiClient.get(endpoint)
    const orgData = orgResp.data?.data || orgResp.data || {}

    // Prepare base structure with data from Organizations API
    const result = {
      organizationName: orgData.organizationName || orgData.name || 'N/A',
      address:
        orgData.address ||
        orgData.billingAddress ||
        orgData.registeredAddress ||
        orgData.addressLine ||
        '',
      gstNumber: orgData.gstNumber || orgData.gstin || orgData.gst || '',
      businessInfo: orgData.businessInfo || orgData.billerDetails || orgData.bankDetails || {},
      raw: orgData,
    }

    // 2) FALLBACK: Try to enrich bank details from Zoho data if businessInfo is incomplete
    if (!result.businessInfo.nameOfTheBank && !result.businessInfo.ifscCode) {
      try {
        const token = localStorage.getItem('accessToken')
        const zohoResp = await fetch(`${API_BASE_URL}zoho/data?organizationId=${orgId}`, {
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        })
        if (zohoResp.ok) {
          const zohoData = await zohoResp.json()
          // Attempt to extract biller/bank details from common shapes in Zoho response
          const bankSources = [
            zohoData?.organization?.businessInfo,
            zohoData?.organization?.billerDetails,
            zohoData?.client?.billerDetails,
            zohoData?.bankDetails,
          ].filter(Boolean)

          const mergedBank = Object.assign({}, ...bankSources)
          // Normalize field names if present under different keys
          const normalizedBank = {
            nameOfTheBank:
              mergedBank.nameOfTheBank || mergedBank.bankName || mergedBank.name || undefined,
            addressOfTheBank:
              mergedBank.addressOfTheBank || mergedBank.bankAddress || mergedBank.address || undefined,
            accountNumber:
              mergedBank.accountNumber || mergedBank.accNumber || mergedBank.account_no || undefined,
            ifscCode:
              mergedBank.ifscCode || mergedBank.ifsc || mergedBank.ifsc_code || undefined,
          }

          // If any bank fields exist, merge into result.businessInfo
          const hasAnyBank = Object.values(normalizedBank).some((v) => v && v !== 'N/A')
          if (hasAnyBank) {
            result.businessInfo = { ...result.businessInfo, ...normalizedBank }
            console.log(`✅ Bank details enriched from Zoho for organization ${orgId}`)
          }
        }
      } catch (zohoErr) {
        // Silent fallback – bank details might not be available via Zoho
        console.warn(`⚠️ Zoho data enrichment skipped for organization ${orgId}:`, zohoErr?.message || zohoErr)
      }
    } else {
      console.log(`✅ Bank details retrieved from Organizations API for organization ${orgId}`)
    }

    return result
  } catch (error) {
    console.error('❌ Error fetching organization details:', organizationId, error)
    return null
  }
}

/**
 * Get last invoice number for organization
 * @param {string} organizationId 
 * @param {string} dateObject 
 * @returns {Promise<Object>}
 */
export const getLastInvoiceNumber = async (organizationId, dateObject) => {
  try {
    const token = localStorage.getItem('accessToken')
    const response = await fetch(`${API_BASE_URL}invoices/lastinvoice/${organizationId}?invocedate=${dateObject}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch last invoice. Status: ${response.status}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching last invoice number:', error)
    return { invoiceNumber: null, reused: false }
  }
}

/**
 * Fetch all invoices with optional filtering
 * @param {Object} params - Query parameters
 * @param {number} params.skip - Number of records to skip
 * @param {number} params.limit - Maximum number of records to return
 * @param {string} params.plantName - Filter by plant name
 * @param {string} params.organizationId - Filter by organization ID
 * @param {string} params.status - Filter by payment status
 * @param {string} params.invoiceNumber - Filter by invoice number
 * @returns {Promise<Object>} Response with data and metadata
 */
export const fetchAllInvoices = async (params = {}) => {
  try {
    const response = await apiClient.get('/invoices', { params })
    const invoices = response.data.invoices || response.data.data || response.data || []

    // Use a local cache to avoid multiple calls for the same organization
    const orgCache = {}

    // Fetch organization names for all invoices and normalize ID field
    const invoicesWithOrgNames = await Promise.all(
      invoices.map(async (invoice) => {
        // Normalize the ID field - backend may return 'id' but frontend expects '_id'
        const normalizedInvoice = {
          ...invoice,
          _id: invoice._id || invoice.id,
        }
        if (normalizedInvoice.organizationId) {
          const orgIdStr = String(normalizedInvoice.organizationId)
          let orgName = orgCache[orgIdStr]
          if (!orgName) {
            orgName = await fetchOrganizationName(normalizedInvoice.organizationId)
            orgCache[orgIdStr] = orgName
          }
          return { ...normalizedInvoice, organizationName: orgName }
        }
        return normalizedInvoice
      })
    )

    return {
      success: true,
      data: invoicesWithOrgNames,
      total: response.data.total || response.data.count || invoices.length,
      message: 'Invoices fetched successfully',
    }
  } catch (error) {
    console.error('Error fetching invoices:', error)
    return {
      success: false,
      data: [],
      total: 0,
      message: error.response?.data?.message || error.message || 'Failed to fetch invoices',
      error,
    }
  }
}

/**
 * Create a new invoice
 * @param {Object} invoiceData - Invoice data
 * @returns {Promise<Object>} Created invoice
 */
export const createInvoice = async (invoiceData) => {
  try {
    const token = localStorage.getItem('accessToken')
    const response = await fetch(`${API_BASE_URL}invoices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(invoiceData)
    })

    if (!response.ok) {
      throw new Error(response.statusText || 'Failed to create invoice')
    }

    return {
      success: true,
      data: await response.json(),
      message: 'Invoice created successfully',
    }
  } catch (error) {
    console.error('Error creating invoice:', error)
    return {
      success: false,
      message: error.message || 'Failed to create invoice',
      error,
    }
  }
}

/**
 * Update invoice status and other fields using PlantBusinessView API
 * Uses the same endpoint: /invoices/:id
 * @param {string} invoiceId - Invoice ID
 * @param {Object} updates - Fields to update (paymentStatus, invoiceDate, dueDate, etc.)
 * @returns {Promise<Object>} Updated invoice
 */
export const updateInvoice = async (invoiceId, updates) => {
  try {
    const token = localStorage.getItem('accessToken')

    // Normalize updates to match PlantBusinessView API format
    const normalizedUpdates = {}

    if (updates.status || updates.paymentStatus) {
      normalizedUpdates.paymentStatus = (updates.status || updates.paymentStatus).toUpperCase()
    }
    if (updates.invoiceDate) {
      normalizedUpdates.invoiceDate = updates.invoiceDate
    }
    if (updates.dueDate) {
      normalizedUpdates.dueDate = updates.dueDate
    }
    if (typeof updates.automated === 'boolean') {
      normalizedUpdates.automated = updates.automated
    }
    if (typeof updates.emailSent === 'boolean') {
      normalizedUpdates.emailSent = updates.emailSent
    }
    if (updates.sentDate) {
      normalizedUpdates.sentDate = updates.sentDate
    }

    const response = await fetch(`${API_BASE_URL}invoices/${invoiceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(normalizedUpdates)
    })

    if (!response.ok) {
      throw new Error(response.statusText || 'Failed to update invoice')
    }

    return {
      success: true,
      data: await response.json(),
      message: 'Invoice updated successfully',
    }
  } catch (error) {
    console.error('Error updating invoice:', error)
    return {
      success: false,
      message: error.message || 'Failed to update invoice',
      error,
    }
  }
}

/**
 * Update invoice email status (used after successful email send)
 * @param {string} invoiceId - Invoice ID
 * @param {boolean} emailSent - Whether email was sent
 * @param {string} sentDate - Date when email was sent (ISO string)
 * @returns {Promise<Object>} Response
 */
export const updateInvoiceEmailStatus = async (invoiceId, emailSent, sentDate) => {
  return await updateInvoice(invoiceId, { emailSent, sentDate })
}

/**
 * Send invoice email to customer using PlantBusinessView email API
 * Uses the same backend endpoint: /invoice-reminder/send-email
 * @param {string} invoiceId - Single invoice ID to send email for
 * @param {string} recipientType - 'customer' or 'team'
 * @returns {Promise<Object>} Response status
 */
/**
 * Send invoice email to customer or team with PDF attachment
 * Matches PlantBusinessView logic and backend requirements
 * @param {string} invoiceId - Invoice ID
 * @param {string} recipientType - 'customer' or 'team'
 * @param {Blob} pdfBlob - The PDF blob to attach
 * @returns {Promise<Object>} Response status
 */
export const sendInvoiceEmail = async (invoiceId, recipientType = 'customer', pdfBlob = null) => {
  try {
    const token = localStorage.getItem('accessToken')
    const formData = new FormData()

    formData.append('invoiceId', invoiceId)
    formData.append('recipientType', recipientType)

    if (pdfBlob) {
      formData.append('pdf', pdfBlob, `Invoice-${invoiceId}.pdf`)
    } else {
      // In PlantBusinessView, the PDF is always generated on frontend.
      // If no blob is provided, the backend will return 400 "Missing PDF file"
      console.warn('sendInvoiceEmail called without pdfBlob. Backend may reject this.')
    }

    const response = await axios.post(
      `${API_BASE_URL}invoice-reminder/send-email`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    )

    if (response.status === 201 || response.status === 200) {
      // If customer email, update sentDate in backend similar to PlantBusinessView
      if (recipientType === 'customer') {
        const currentDate = new Date().toISOString()
        await updateInvoice(invoiceId, { emailSent: true, sentDate: currentDate })
      }

      return {
        success: true,
        data: response.data,
        message: `Email sent successfully to ${recipientType}`,
      }
    } else {
      throw new Error('Failed to send email')
    }
  } catch (error) {
    console.error(`Error sending email to ${recipientType} for invoice ${invoiceId}:`, error)
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to send email',
      error,
    }
  }
}

/**
 * Send bulk invoice emails
 * @param {Array<string>} invoiceIds - Array of invoice IDs
 * @param {string} recipientType - 'customer' or 'team'
 * @returns {Promise<Object>} Response status with results for each invoice
 */
/**
 * Send bulk invoice emails
 * @param {Array<string>} invoiceIds - Array of invoice IDs
 * @param {string} recipientType - 'customer' or 'team'
 * @param {Blob} pdfBlob - Optional PDF blob for single invoice
 * @returns {Promise<Object>} Response status with results for each invoice
 */
export const sendBulkInvoiceEmails = async (invoiceIds, recipientType = 'customer', pdfBlob = null) => {
  const results = []

  for (const invoiceId of invoiceIds) {
    // If we have multiple IDs but only one PDF blob, we can only use the blob for the matching ID
    // Otherwise, we pass null and rely on the calling function to provide PDFs
    const currentBlob = invoiceIds.length === 1 ? pdfBlob : null
    const result = await sendInvoiceEmail(invoiceId, recipientType, currentBlob)
    results.push({ invoiceId, ...result })
  }

  const successCount = results.filter(r => r.success).length
  const failCount = results.filter(r => !r.success).length

  return {
    success: failCount === 0,
    message: `Sent ${successCount}/${invoiceIds.length} emails${failCount > 0 ? `, ${failCount} failed` : ''}`,
    results
  }
}


/**
 * Get invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Invoice details
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    console.log('Fetching invoice by ID:', invoiceId)
    const response = await apiClient.get(`/invoices/${invoiceId}`)
    console.log('Invoice API response:', response.data)

    // Handle different response formats - data could be nested or direct
    const rawData = response.data?.data || response.data

    // Normalize the ID field
    const invoiceData = rawData ? {
      ...rawData,
      _id: rawData._id || rawData.id,
    } : null

    console.log('Extracted invoice data:', invoiceData)

    return {
      success: true,
      data: invoiceData,
      message: 'Invoice fetched successfully',
    }
  } catch (error) {
    console.error('Error fetching invoice:', invoiceId, error)
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Failed to fetch invoice',
      error,
    }
  }
}

/**
 * Send invoice email to team with properly generated PDF
 * Uses the same PDF generation logic as PlantBusinessView (html2canvas + jsPDF)
 * @param {string} invoiceId - Invoice ID
 * @param {string} recipientType - 'team' or 'customer'
 * @param {HTMLElement} pdfElement - The DOM element to capture for PDF (InvoicePdfPreview)
 * @returns {Promise<Object>} Result with success status and message
 */
/**
 * Send invoice email with properly generated PDF from a DOM element
 * This is a high-level wrapper that coordinates PDF generation and transmission
 * @param {string} invoiceId - Invoice ID
 * @param {string} recipientType - 'team' or 'customer'
 * @param {HTMLElement} pdfElement - The DOM element to capture for PDF
 * @returns {Promise<Object>} Result with success status and message
 */
export const sendInvoiceEmailToTeam = async (invoiceId, recipientType = 'team', pdfElement = null) => {
  try {
    if (!invoiceId) {
      throw new Error('Invoice ID is required')
    }

    // Generate PDF using the provided element or find it by ID
    let pdfBlob = null

    if (pdfElement) {
      pdfBlob = await generateInvoicePdfBlob(pdfElement, false)
    } else {
      const previewElement = document.getElementById('invoice-pdf-preview')
      if (!previewElement) {
        throw new Error('Invoice preview element not found. PDF generation requires the invoice preview to be rendered.')
      }
      pdfBlob = await generateInvoicePdfBlob(previewElement, false)
    }

    if (!pdfBlob) {
      throw new Error('Failed to generate PDF')
    }

    // Use the generic send function to transmit the email
    return await sendInvoiceEmail(invoiceId, recipientType, pdfBlob)
  } catch (error) {
    console.error(`❌ Error in sendInvoiceEmailToTeam for ${recipientType}:`, error)
    return {
      success: false,
      message: error.message || 'Failed to send email',
      error,
    }
  }
}

/**
 * Generate PDF blob from an HTML element using html2canvas (same as PlantBusinessView)
 * @param {HTMLElement} element - The DOM element to capture
 * @param {boolean} forEmail - Whether to optimize for email (lower quality)
 * @returns {Promise<Blob>} PDF blob
 */
export const generateInvoicePdfBlob = async (element, forEmail = false) => {
  try {
    if (!element) {
      throw new Error('Element is required for PDF generation')
    }

    const pdf = new jsPDF('p', 'mm', 'a4')

    // Hide interactive elements
    const editButtons = element.querySelectorAll('.MuiIconButton-root, button')
    editButtons.forEach((button) => (button.style.display = 'none'))

    const actionColumns = element.querySelectorAll('.action-column, .actions')
    actionColumns.forEach((col) => (col.style.display = 'none'))

    // Hide email-specific elements if optimizing for email
    const pdfHideElements = element.querySelectorAll('.pdf-hide')
    if (forEmail) {
      pdfHideElements.forEach((el) => (el.style.display = 'none'))
    }

    // Capture element as canvas using html2canvas
    const canvas = await html2canvas(element, {
      scale: forEmail ? 1.5 : 2,
      useCORS: true,
      logging: false,
      imageTimeout: 0,
      removeContainer: true,
      letterRendering: true,
    })

    const imgData = canvas.toDataURL('image/jpeg', forEmail ? 0.7 : 1.0)

    const elementWidth = element.getBoundingClientRect().width
    const elementHeight = element.getBoundingClientRect().height
    const scaleFactor = pdf.internal.pageSize.getWidth() / elementWidth

    pdf.addImage(
      imgData,
      'JPEG',
      0,
      0,
      pdf.internal.pageSize.getWidth(),
      elementHeight * scaleFactor
    )

    // Revert UI changes
    editButtons.forEach((button) => (button.style.display = 'inline'))
    actionColumns.forEach((col) => (col.style.display = 'inline'))
    if (forEmail) {
      pdfHideElements.forEach((el) => (el.style.display = ''))
    }

    // Convert PDF to Blob
    return pdf.output('blob')
  } catch (error) {
    console.error('Error generating PDF blob:', error)
    throw error
  }
}

/**
 * Delete invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Response
 */
export const deleteInvoice = async (invoiceId) => {
  try {
    const response = await apiClient.delete(`/invoices/${invoiceId}`)
    return {
      success: true,
      message: 'Invoice deleted successfully',
      data: response.data
    }
  } catch (error) {
    console.error('Error deleting invoice:', invoiceId, error)
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to delete invoice',
      error,
    }
  }
}


/**
 * Fetch email recipients for a plan
 * @param {string} planId - Plan ID
 * @returns {Promise<Object>} { to: [...], cc: [...] }
 */
export const fetchPlanEmailRecipients = async (planId) => {
  try {
    const response = await apiClient.get(`/invoices/plan/${planId}/email-recipients`)
    return response.data
  } catch (error) {
    console.error('Error fetching plan email recipients:', error)
    return { to: [], cc: [] }
  }
}

/**
 * Fetch team email recipients
 * @returns {Promise<Object>} { to: [...], cc: [...] }
 */
export const fetchTeamEmailRecipients = async () => {
  try {
    const response = await apiClient.get('/invoices/team/email-recipients')
    return response.data
  } catch (error) {
    console.error('Error fetching team email recipients:', error)
    return { to: [], cc: [] }
  }
}
